import * as T from 'three';
import {cliffHit} from '@hyperlight/shared';
import type {PlanetHost} from '../world/planetHost';
import type {DeathContactResolver} from './deathPhysics';

/** Share the living world's solid geometry with corpse joints and crystal debris.
 * Sweeps stop fast bodies at thin walls/decks, independent of the render origin. */
export function makePlanetDeathResolver(host:()=>PlanetHost|null,origin:()=>T.Vector3,renderFrame?:()=>T.Object3D|null):DeathContactResolver {
  const a=new T.Vector3(),b=new T.Vector3(),p=new T.Vector3(),dir=new T.Vector3(),n=new T.Vector3(),delta=new T.Vector3();
  const output={x:0,y:0,z:0},hit=cliffHit(),normalArray=[0,1,0];
  const inverse=new T.Matrix4(),renderRotation=new T.Quaternion();
  return (from,position,radius,normal)=>{
    const planet=host();if(!planet)return false;
    const frame=planet.pose,offset=origin();
    a.copy(from).add(offset).sub(frame.center).applyQuaternion(frame.invQuat);
    b.copy(position).add(offset).sub(frame.center).applyQuaternion(frame.invQuat);
    const rendered=renderFrame?.();
    if(rendered){rendered.updateWorldMatrix(true,false);inverse.copy(rendered.matrixWorld).invert();a.copy(from).applyMatrix4(inverse);b.copy(position).applyMatrix4(inverse);}
    let touched=false;
    const contact=(fraction:number,outward:T.Vector3,penetration=0)=>{
      delta.subVectors(b,a);p.copy(a).addScaledVector(delta,Math.max(0,fraction));
      delta.multiplyScalar(1-fraction);delta.addScaledVector(outward,-Math.min(0,delta.dot(outward)));
      b.copy(p).add(delta).addScaledVector(outward,penetration+.002);
      n.copy(outward);touched=true;
    };
    const boxes=planet.settlementColliders;
    const box=boxes?.segmentCast(a.x,a.y,a.z,b.x,b.y,b.z,radius);
    if(box){
      dir.fromArray(box.n);contact(box.t,dir);
      // Death may start while a contact animation already overlaps a wall.
      // Resolve that initial overlap along its nearest face, including roofs.
      for(let i=0;i<40&&boxes!.occludes(b.x,b.y,b.z,radius);i++)b.addScaledVector(dir,.05);
    }
    const placed=planet.homesteadSegmentHit?.(a.x,a.y,a.z,b.x,b.y,b.z,radius)??-1;
    if(placed>=0){
      p.lerpVectors(a,b,placed);dir.subVectors(a,b).normalize();
      // Recover the placed face normal from its inflated occupancy boundary.
      if(planet.homesteadOccludes){
        const solid=planet.homesteadOccludes,e=.005;
        dir.set(Number(solid(p.x-e,p.y,p.z,radius))-Number(solid(p.x+e,p.y,p.z,radius)),
          Number(solid(p.x,p.y-e,p.z,radius))-Number(solid(p.x,p.y+e,p.z,radius)),
          Number(solid(p.x,p.y,p.z-e,radius))-Number(solid(p.x,p.y,p.z+e,radius)));
        if(dir.lengthSq()<.1)dir.subVectors(a,b);dir.normalize();
      }
      contact(placed,dir);
    }
    if(planet.terrainSolids?.sphereSweep(a.toArray(),b.toArray(),radius,hit))contact(hit.t,dir.fromArray(hit.normal),hit.penetration);
    // Short samples are needed for props whose host offers point push-out only.
    const steps=Math.max(1,Math.ceil(a.distanceTo(b)/Math.max(.06,radius*.6)));
    for(let i=1;i<=steps;i++){
      p.lerpVectors(a,b,i/steps);
      dir.copy(p).applyQuaternion(frame.quat).normalize();
      const surface=planet.surfaceRadiusWorld(dir),distance=p.length();
      if(distance<surface+radius){
        planet.surfaceNormalWorld(dir,normalArray);n.fromArray(normalArray).applyQuaternion(frame.invQuat).normalize();
        p.multiplyScalar((surface+radius+.002)/Math.max(.001,distance));b.copy(p);touched=true;break;
      }
      for(const resolve of [planet.resolveScatterCollision,planet.resolveHomesteadCollision]){
        if(resolve?.call(planet,p.x,p.y,p.z,radius,output)){
          n.set(output.x-p.x,output.y-p.y,output.z-p.z).normalize();
          b.set(output.x,output.y,output.z);touched=true;break;
        }
      }
      if(touched&&i<steps&&p.distanceToSquared(b)<radius*radius)break;
    }
    if(!touched)return false;
    position.copy(b).applyQuaternion(frame.quat).add(frame.center).sub(offset);
    normal.copy(n).applyQuaternion(frame.quat).normalize();
    if(rendered){position.copy(b).applyMatrix4(rendered.matrixWorld);normal.copy(n).applyQuaternion(rendered.getWorldQuaternion(renderRotation)).normalize();}
    return true;
  };
}
